/**
 * Service de reconnaissance locale via Hardware Bridge → Ollama moondream2
 *
 * Appelle le bridge local (HTTPS localhost:3334) qui proxie vers Ollama.
 * Retourne un nom anglais brut (ex: "tomato", "carrot") ou null si indisponible.
 */

import { logger } from '../../utils/logger';
import { enTetesBridgeJson } from '../bridgeHeaders';

const BRIDGE_URL = 'https://localhost:3334';
const RECOGNIZE_TIMEOUT = 8000; // 8s max (inclut le temps Ollama)

export interface LocalRecognitionResult {
  name: string;       // Nom anglais brut (ex: "tomato")
  rawResponse: string; // Réponse complète de moondream
  durationMs: number;
}

export interface LocalRecognitionStatus {
  available: boolean;
  model: string;
  ollamaRunning: boolean;
  modelsAvailable?: string[];
  error?: string;
}

/**
 * Envoyer une image au bridge pour reconnaissance locale via Ollama moondream2.
 * Retourne null si le bridge ou Ollama n'est pas disponible.
 */
export async function recognizeLocal(imageBase64: string): Promise<LocalRecognitionResult | null> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), RECOGNIZE_TIMEOUT);

  try {
    const res = await fetch(`${BRIDGE_URL}/recognize`, {
      method: 'POST',
      headers: enTetesBridgeJson(),
      body: JSON.stringify({ image: imageBase64 }),
      signal: controller.signal,
    });
    clearTimeout(timeout);

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      logger.log(`[LocalRecognition] Bridge returned ${res.status}:`, data.error || 'unknown error');
      return null;
    }

    const data = await res.json();

    if (!data.name || data.name === 'unknown' || data.name === 'inconnu') {
      logger.log('[LocalRecognition] Moondream could not identify product');
      return null;
    }

    logger.log(`[LocalRecognition] Identified: "${data.name}" (raw: "${data.rawResponse}") in ${data.durationMs}ms`);
    return data;
  } catch (err: any) {
    clearTimeout(timeout);

    if (err.name === 'AbortError') {
      logger.log('[LocalRecognition] Timeout — bridge/Ollama too slow');
    } else {
      // Bridge not reachable (HTTPS connection refused, etc.)
      logger.log('[LocalRecognition] Bridge not reachable:', err.message);
    }
    return null;
  }
}

export interface LocalLabelResult {
  dlc: string | null;
  lot_number: string | null;
  date_type: 'DLC' | 'DDM' | null;
  confidence: number;
  /** D'où vient la lecture : OCR Apple du bridge, ou repli Claude Vision. */
  source: 'local' | 'cloud';
  /**
   * Lignes de texte détectées avec leur POSITION, telles qu'Apple Vision les rend.
   * Repère Vision : fractions 0..1, origine EN BAS À GAUCHE — un `y` de 0,9 est
   * donc en HAUT de l'image. Sert à pré-tracer les zones à masquer dans l'éditeur.
   */
  lines?: Array<{ text: string; confidence: number; x: number; y: number; w: number; h: number }>;
}

/** Une zone de texte repérée sur la photo, déjà convertie dans le repère de l'écran. */
export interface TextZone {
  text: string;
  /** Fractions 0..1, origine EN HAUT à gauche (repère CSS et repère du serveur). */
  x: number;
  y: number;
  width: number;
  height: number;
  /** Ce que le texte a l'air d'être — sert à ne proposer que ce qui se masque. */
  kind: 'date' | 'price' | 'other';
}

const RE_DATE =
  /(\d{1,2}\s*[./-]\s*\d{1,2}\s*[./-]\s*\d{2,4})|(\d{1,2}\s*(janv|f[ée]vr|mars|avr|mai|juin|juil|ao[uû]t|sept|oct|nov|d[ée]c)[a-z.]*\s*\d{2,4})|\b(20\d{2})[-/](\d{1,2})[-/](\d{1,2})\b/i;
const RE_PRICE = /(\d+[.,]\d{2}\s*€)|(€\s*\d+[.,]\d{2})|(\d+[.,]\d{2}\s*(eur|euros?))/i;

/**
 * Convertit les lignes d'Apple Vision en zones masquables.
 *
 * Deux conversions à ne pas rater : l'origine (Vision compte depuis le BAS, le web
 * depuis le HAUT) et la marge — un cadre au ras des lettres laisse dépasser les
 * jambages, on l'élargit légèrement.
 */
export function textZonesFromLines(
  lines: LocalLabelResult['lines'],
  marge = 0.01,
): TextZone[] {
  if (!lines?.length) return [];
  return lines
    .map((l) => {
      const texte = (l.text || '').trim();
      const kind: TextZone['kind'] = RE_DATE.test(texte)
        ? 'date'
        : RE_PRICE.test(texte)
          ? 'price'
          : 'other';
      return {
        text: texte,
        kind,
        x: Math.max(0, l.x - marge),
        // Vision : origine en bas → le haut du cadre est 1 - (y + h).
        y: Math.max(0, 1 - (l.y + l.h) - marge),
        width: Math.min(1, l.w + marge * 2),
        height: Math.min(1, l.h + marge * 2),
      };
    })
    .filter((z) => z.width > 0 && z.height > 0);
}

/**
 * Une date limite déjà passée le jour de la réception est une lecture fausse : sur 80
 * vraies étiquettes, le lecteur a proposé « 2010 » pris dans un code (25/10/2026). On ne
 * pré-remplit alors rien — les positions du texte restent, l'éditeur photo s'en sert.
 * Doublé dans `dlc-parser.js` ; ici pour les caisses dont le bridge n'est pas à jour.
 */
export function sansDateDejaPassee<T extends { dlc: string | null } | null>(data: T, aujourdhui = dateLocaleISO()): T {
  if (!data?.dlc || data.dlc >= aujourdhui) return data;
  return { ...data, dlc: null, date_type: null, confidence: 0 };
}

function dateLocaleISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Lecture DLC + n° de lot d'une photo d'étiquette, 100 % LOCALE (Apple Vision OCR + analyseur,
 * via le bridge `/ocr-label`). Aucun cloud, aucun coût. Retourne null si le bridge est
 * injoignable (l'opérateur saisira alors la DLC à la main).
 */
export async function extractLabelLocal(imageBase64: string): Promise<LocalLabelResult | null> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 9000);
  try {
    const res = await fetch(`${BRIDGE_URL}/ocr-label`, {
      method: 'POST',
      headers: enTetesBridgeJson(),
      body: JSON.stringify({ image: imageBase64 }),
      signal: controller.signal,
    });
    clearTimeout(timeout);
    if (!res.ok) {
      logger.log(`[LabelOCR] Bridge returned ${res.status}`);
      return null;
    }
    const data = await res.json();
    logger.log(`[LabelOCR] DLC=${data?.dlc ?? 'aucune'} (${data?.date_type ?? '-'}, conf ${data?.confidence})`);
    return sansDateDejaPassee(data);
  } catch (err: any) {
    clearTimeout(timeout);
    logger.log('[LabelOCR] Bridge injoignable:', err?.message);
    return null;
  }
}

/**
 * Vérifier le statut d'Ollama via le bridge.
 * Retourne le statut même si le bridge n'est pas disponible.
 */
export async function checkLocalRecognitionStatus(): Promise<LocalRecognitionStatus> {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);

    const res = await fetch(`${BRIDGE_URL}/recognize/status`, {
      signal: controller.signal,
    });
    clearTimeout(timeout);

    if (!res.ok) {
      return { available: false, model: 'moondream', ollamaRunning: false };
    }

    return await res.json();
  } catch {
    return { available: false, model: 'moondream', ollamaRunning: false };
  }
}
